import React, { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Trophy,
  Zap,
  CheckCircle,
  XCircle,
  Users,
} from "lucide-react";
import { timeAttackAPI } from "../api";
import { useLang } from "../context/LangContext";
import { useAuth } from "../context/AuthContext";

const IMG_BASE = process.env.REACT_APP_BASE_URL_IMAGE;
const DEFAULT_IMG = `https://avtomind.uz/storage/photos/default.webp`;
const BONUS_SECONDS = 5;
const START_SECONDS = 60;

interface Question {
  id: number;
  question: string;
  photo_url?: string;
  correct_answer_index: number;
  choices: { id: number; order_index: number; text: string }[];
  explanation?: string;
}

interface LeaderEntry {
  rank: number;
  totalScore: number;
  correctCount: number;
  totalQuestions: number;
  user: { id: string; name: string; photo?: string } | null;
}

type Phase = "start" | "playing" | "answered" | "finished";

const LABELS = ["A", "B", "C", "D", "E", "F"];

const TimeAttackPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { lang } = useLang();

  const [phase, setPhase] = useState<Phase>("start");
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [question, setQuestion] = useState<Question | null>(null);
  const [timeLeft, setTimeLeft] = useState(START_SECONDS);
  const [score, setScore] = useState(0);
  const [correctCount, setCorrectCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [selected, setSelected] = useState<number | null>(null);
  const [isCorrect, setIsCorrect] = useState<boolean | null>(null);
  const [explanation, setExplanation] = useState<string>("");
  const [bonusAnim, setBonusAnim] = useState(false);
  const [leaderboard, setLeaderboard] = useState<LeaderEntry[]>([]);
  const [showLeaderboard, setShowLeaderboard] = useState(false);
  const [loading, setLoading] = useState(false);
  const [imgLoaded, setImgLoaded] = useState(false);

  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const finishedRef = useRef(false);


  // ── Timer ──
  useEffect(() => {
    if (phase !== "playing" && phase !== "answered") return;
    timerRef.current = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timerRef.current!);
          handleFinish();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current!);
  }, [phase]);

  // ── Start ──
  const handleStart = async () => {
    setLoading(true);
    try {
      const res = await timeAttackAPI.start({ lang, topicId: null });
      const sid = res.data.sessionId || res.data.id;
      setSessionId(sid);
      setTimeLeft(START_SECONDS);
      setScore(0);
      setCorrectCount(0);
      setTotalCount(0);
      finishedRef.current = false;
      await loadNext(sid);
      setPhase("playing");
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  // ── Load next question ──
  const loadNext = async (sid: string) => {
    setImgLoaded(false);
    setSelected(null);
    setIsCorrect(null);
    setExplanation("");
    const res = await timeAttackAPI.nextQuestion(sid);
    const q = res.data?.question || res.data;
    setQuestion({
      id: q.id,
      question: q[`question_${lang}`] || q.question_uz || q.question,
      photo_url: q.photo_url || q.image,
      correct_answer_index: q.correct_answer_index,
      choices: (q.choices || q.answers || []).map((c: any) => ({
        id: c.id,
        order_index: c.order_index,
        text: c[`text_${lang}`] || c.text_uz || c.text,
      })),
    });
  };

  // ── Answer ──
  const handleAnswer = async (orderIndex: number, choiceId: number) => {
    if (!sessionId || !question || selected !== null) return;
    setSelected(orderIndex);
    setTotalCount((p) => p + 1);

    try {
      const res = await timeAttackAPI.submitAnswer(sessionId, {
        questionId: question.id,
        choiceId: choiceId,
        timeRemaining: timeLeft,
      });

      const correct = !!res.data.isCorrect;
      const exp =
        res.data.explanation_oz ||
        res.data.explanation_uz ||
        res.data.explanation ||
        "";

      setIsCorrect(correct);
      setExplanation(exp);

      if (correct) {
        setCorrectCount((p) => p + 1);
        setScore((p) => p + 20);
        setTimeLeft((p) => p + (res.data.bonusTime || BONUS_SECONDS));
        setBonusAnim(true);
        setTimeout(() => setBonusAnim(false), 1200);
      }

      setPhase("answered");

      // Auto next after 1.5s
      setTimeout(() => {
        if (finishedRef.current) return;
        const next = res.data.nextQuestion;
        if (next) {
          setQuestion({
            id: next.id,
            question: next[`question_${lang}`] || next.question_oz,
            photo_url: next.photo_url,
            correct_answer_index: next.correct_answer_index,
            choices: next.choices.map((c: any) => ({
              id: c.id,
              order_index: c.order_index,
              text: c[`text_${lang}`] || c.text_oz,
            })),
          });
          console.log("Yuborilgan choiceId:", choiceId);
          console.log("Backend correctChoiceId:", res.data.correctChoiceId);
          console.log("isCorrect:", res.data.isCorrect);
          setSelected(null);
          setIsCorrect(null);
          setExplanation("");
          setImgLoaded(false);
          setPhase("playing");
        } else {
          handleFinish();
        }
      }, 1500);
    } catch (e) {
      console.error(e);
    }
  };

  // ── Finish ──
  const handleFinish = useCallback(async () => {
    if (finishedRef.current) return;
    finishedRef.current = true;
    clearInterval(timerRef.current!);
    if (sessionId) {
      try {
        await timeAttackAPI.finish(sessionId, 0);
      } catch (e) {
        console.error(e);
      }
    }
    // Load leaderboard
    try {
      const res = await timeAttackAPI.leaderboard(10);
      setLeaderboard(res.data || []);
    } catch (e) {
      console.error(e);
    }
    setPhase("finished");
  }, [sessionId]);

  const fmt = (s: number) =>
    `${Math.floor(s / 60)
      .toString()
      .padStart(2, "0")}:${(s % 60).toString().padStart(2, "0")}`;

  const timerColor =
    timeLeft <= 10 ? "#EF4444"
    : timeLeft <= 20 ? "#F59E0B"
    : "#22C55E";

  // ── START SCREEN ──
  if (phase === "start") {
    return (
      <div style={S.page}>
        <style>{css}</style>
        {/* Header */}
        <div style={S.nav}>
          <button onClick={() => navigate("/dashboard")} style={S.backBtn}>
            <ArrowLeft size={18} />
          </button>
        </div>

        <div
          style={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            padding: "24px 20px",
            textAlign: "center",
          }}>
          {/* Hero */}
          <div style={{ marginBottom: 32 }}>
            <div
              style={{
                fontSize: 72,
                marginBottom: 16,
                filter: "drop-shadow(0 0 24px rgba(245,158,11,.5))",
              }}>
              ⏱
            </div>
            <div
              style={{
                background: "linear-gradient(135deg,#F59E0B,#EF4444)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                fontWeight: 900,
                fontSize: "clamp(28px,6vw,48px)",
                letterSpacing: -1,
                lineHeight: 1.1,
              }}>
              TIME ATTACK
            </div>
            <p style={{ color: "#64748B", fontSize: 14, marginTop: 8 }}>
              Iloji boricha ko'p savol yeching!
            </p>
          </div>

          {/* Rules */}
          <div
            style={{
              background: "#111827",
              border: "1px solid #1E2D45",
              borderRadius: 20,
              padding: "20px 24px",
              maxWidth: 340,
              width: "100%",
              marginBottom: 32,
              textAlign: "left",
            }}>
            {[
              {
                icon: "⏱",
                color: "#22C55E",
                text: `Boshlang'ich vaqt: ${START_SECONDS} soniya`,
              },
              {
                icon: "✅",
                color: "#3B82F6",
                text: `To'g'ri javob: +${BONUS_SECONDS} soniya qo'shiladi`,
              },
              {
                icon: "❌",
                color: "#EF4444",
                text: "Xato javob: vaqt qo'shilmaydi",
              },
              {
                icon: "🏆",
                color: "#F59E0B",
                text: "Eng ko'p ball → Leaderboard!",
              },
            ].map((r, i) => (
              <div
                key={i}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  marginBottom: i < 3 ? 12 : 0,
                }}>
                <span style={{ fontSize: 20, flexShrink: 0 }}>{r.icon}</span>
                <span
                  style={{ color: "#CBD5E1", fontSize: 13, fontWeight: 600 }}>
                  {r.text}
                </span>
              </div>
            ))}
          </div>

          <button
            onClick={handleStart}
            disabled={loading}
            className="pulse-btn"
            style={{
              padding: "16px 48px",
              borderRadius: 16,
              border: "none",
              background: "linear-gradient(135deg,#F59E0B,#EF4444)",
              color: "#fff",
              fontWeight: 900,
              fontSize: 18,
              cursor: "pointer",
              boxShadow: "0 8px 32px rgba(245,158,11,.4)",
              opacity: loading ? 0.7 : 1,
            }}>
            {loading ? "Yuklanmoqda..." : "🚀 Boshlash"}
          </button>

          <button
            onClick={async () => {
              const res = await timeAttackAPI.leaderboard(10);
              setLeaderboard(res.data || []);
              setShowLeaderboard(true);
            }}
            style={{
              marginTop: 16,
              background: "transparent",
              border: "1px solid #1E2D45",
              borderRadius: 12,
              color: "#64748B",
              padding: "10px 24px",
              cursor: "pointer",
              fontSize: 13,
              fontWeight: 600,
              display: "flex",
              alignItems: "center",
              gap: 6,
            }}>
            <Trophy size={14} /> Leaderboard ko'rish
          </button>
        </div>

        {showLeaderboard && (
          <LeaderboardModal
            data={leaderboard}
            userId={user?.id?.toString()}
            onClose={() => setShowLeaderboard(false)}
          />
        )}
      </div>
    );
  }

  // ── FINISHED SCREEN ──
  if (phase === "finished") {
    const myRank =
      leaderboard.findIndex((e) => e.user?.id === String(user?.id)) + 1;
    return (
      <div style={S.page}>
        <style>{css}</style>
        <div
          style={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "flex-start",
            padding: "40px 20px 24px",
            overflowY: "auto",
          }}>
          {/* Result card */}
          <div
            style={{
              background: "#111827",
              border: "1px solid rgba(245,158,11,.3)",
              borderRadius: 24,
              padding: "28px 24px",
              maxWidth: 400,
              width: "100%",
              textAlign: "center",
              marginBottom: 20,
            }}>
            <div style={{ fontSize: 52, marginBottom: 8 }}>🏁</div>
            <div
              style={{
                color: "#fff",
                fontWeight: 900,
                fontSize: 22,
                marginBottom: 4,
              }}>
              Vaqt tugadi!
            </div>
            <div style={{ color: "#64748B", fontSize: 13, marginBottom: 24 }}>
              Ajoyib o'yin!
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr 1fr",
                gap: 12,
                marginBottom: 20,
              }}>
              {[
                { label: "Ball", value: score, color: "#F59E0B" },
                { label: "To'g'ri", value: correctCount, color: "#22C55E" },
                { label: "Jami", value: totalCount, color: "#3B82F6" },
              ].map((s, i) => (
                <div
                  key={i}
                  style={{
                    background: "#0D1526",
                    borderRadius: 12,
                    padding: "12px 8px",
                  }}>
                  <div
                    style={{ color: s.color, fontWeight: 900, fontSize: 24 }}>
                    {s.value}
                  </div>
                  <div
                    style={{ color: "#64748B", fontSize: 10, fontWeight: 700 }}>
                    {s.label}
                  </div>
                </div>
              ))}
            </div>

            {myRank > 0 && (
              <div
                style={{
                  background: "rgba(245,158,11,.1)",
                  border: "1px solid rgba(245,158,11,.3)",
                  borderRadius: 12,
                  padding: "10px 16px",
                  marginBottom: 16,
                }}>
                <span style={{ color: "#F59E0B", fontWeight: 800 }}>
                  #{myRank} o'rinda
                </span>
                <span style={{ color: "#64748B", fontSize: 13 }}>
                  {" "}
                  leaderboardda
                </span>
              </div>
            )}
          </div>

          {/* Leaderboard */}
          {leaderboard.length > 0 && (
            <div
              style={{
                background: "#111827",
                border: "1px solid #1E2D45",
                borderRadius: 20,
                padding: "20px",
                maxWidth: 400,
                width: "100%",
                marginBottom: 20,
              }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  marginBottom: 16,
                }}>
                <Trophy size={16} color="#F59E0B" />
                <span style={{ color: "#fff", fontWeight: 800, fontSize: 15 }}>
                  Top o'yinchilar
                </span>
              </div>
              {leaderboard.map((e, i) => {
                const isMe = e.user?.id === String(user?.id);
                const medals = ["🥇", "🥈", "🥉"];
                return (
                  <div
                    key={i}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 12,
                      padding: "10px 12px",
                      borderRadius: 12,
                      background:
                        isMe ? "rgba(245,158,11,.1)"
                        : i % 2 === 0 ? "rgba(255,255,255,.02)"
                        : "transparent",
                      border:
                        isMe ?
                          "1px solid rgba(245,158,11,.3)"
                        : "1px solid transparent",
                      marginBottom: 6,
                    }}>
                    <span
                      style={{
                        fontSize: 16,
                        minWidth: 24,
                        textAlign: "center",
                      }}>
                      {medals[i] || `${i + 1}`}
                    </span>
                    <span
                      style={{
                        color: isMe ? "#F59E0B" : "#CBD5E1",
                        fontWeight: isMe ? 800 : 600,
                        fontSize: 13,
                        flex: 1,
                      }}>
                      {isMe ? "Siz" : e.user?.name || "user"}
                    </span>
                    <span style={{ color: "#64748B", fontSize: 12 }}>
                      {e.correctCount} ta savol
                    </span>
                    <span
                      style={{
                        color: "#F59E0B",
                        fontWeight: 800,
                        fontSize: 13,
                        minWidth: 50,
                        textAlign: "right",
                      }}>
                      {e.totalScore} ball
                    </span>
                  </div>
                );
              })}
            </div>
          )}

          <div
            style={{ display: "flex", gap: 10, maxWidth: 400, width: "100%" }}>
            <button
              onClick={() => navigate("/dashboard")}
              style={{
                flex: 1,
                padding: "13px",
                borderRadius: 14,
                border: "1px solid #1E2D45",
                background: "#111827",
                color: "#fff",
                fontWeight: 700,
                fontSize: 14,
                cursor: "pointer",
              }}>
              Menu
            </button>
            <button
              onClick={() => {
                setPhase("start");
                finishedRef.current = false;
              }}
              style={{
                flex: 1,
                padding: "13px",
                borderRadius: 14,
                border: "none",
                background: "linear-gradient(135deg,#F59E0B,#EF4444)",
                color: "#fff",
                fontWeight: 900,
                fontSize: 14,
                cursor: "pointer",
              }}>
              Qayta o'ynash
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── PLAYING / ANSWERED ──
  return (
    <div style={S.page}>
      <style>{css}</style>

      {/* ── TOP BAR ── */}
      <div
        style={{
          background: "#0A0E1A",
          borderBottom: "1px solid #1E2D45",
          padding: "10px 16px",
          flexShrink: 0,
        }}>
        {/* Nav row */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: 12,
            maxWidth: 700,
            margin: "0 auto 12px",
          }}>
          <button onClick={() => navigate("/dashboard")} style={S.backBtn}>
            <ArrowLeft size={16} />
          </button>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <Zap size={14} color="#F59E0B" />
            <span
              style={{
                color: "#fff",
                fontWeight: 900,
                fontSize: 14,
                letterSpacing: 1,
              }}>
              TIME ATTACK
            </span>
          </div>
          <button
            onClick={async () => {
              const res = await timeAttackAPI.leaderboard(10);
              setLeaderboard(res.data || []);
              setShowLeaderboard(true);
            }}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 5,
              padding: "5px 10px",
              borderRadius: 8,
              border: "1px solid #1E2D45",
              background: "transparent",
              color: "#64748B",
              fontSize: 11,
              fontWeight: 700,
              cursor: "pointer",
            }}>
            <Users size={12} /> Top
          </button>
        </div>

        {/* Stats row */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr 1fr 1fr",
            gap: 8,
            maxWidth: 700,
            margin: "0 auto",
          }}>
          {/* Timer */}
          <div
            style={{
              background: `${timerColor}15`,
              border: `1px solid ${timerColor}40`,
              borderRadius: 12,
              padding: "8px 4px",
              textAlign: "center",
              position: "relative",
            }}>
            {bonusAnim && (
              <div
                className="bonus-anim"
                style={{
                  position: "absolute",
                  top: -20,
                  left: "50%",
                  transform: "translateX(-50%)",
                  color: "#22C55E",
                  fontWeight: 900,
                  fontSize: 13,
                  whiteSpace: "nowrap",
                }}>
                +{BONUS_SECONDS}s ⬆
              </div>
            )}
            <div
              style={{
                color: timerColor,
                fontWeight: 900,
                fontSize: "clamp(16px,4vw,22px)",
                fontFamily: "monospace",
                lineHeight: 1,
              }}>
              {fmt(timeLeft)}
            </div>
            <div
              style={{
                color: timerColor,
                fontSize: 9,
                fontWeight: 700,
                marginTop: 2,
                opacity: 0.7,
              }}>
              VAQT
            </div>
          </div>
          {/* Questions */}
          <div
            style={{
              background: "#111827",
              border: "1px solid #1E2D45",
              borderRadius: 12,
              padding: "8px 4px",
              textAlign: "center",
            }}>
            <div
              style={{
                color: "#3B82F6",
                fontWeight: 900,
                fontSize: "clamp(16px,4vw,22px)",
                lineHeight: 1,
              }}>
              {totalCount}
            </div>
            <div
              style={{
                color: "#64748B",
                fontSize: 9,
                fontWeight: 700,
                marginTop: 2,
              }}>
              SAVOL
            </div>
          </div>
          {/* Correct */}
          <div
            style={{
              background: "#111827",
              border: "1px solid #1E2D45",
              borderRadius: 12,
              padding: "8px 4px",
              textAlign: "center",
            }}>
            <div
              style={{
                color: "#22C55E",
                fontWeight: 900,
                fontSize: "clamp(16px,4vw,22px)",
                lineHeight: 1,
              }}>
              {correctCount}
            </div>
            <div
              style={{
                color: "#64748B",
                fontSize: 9,
                fontWeight: 700,
                marginTop: 2,
              }}>
              TO'G'RI
            </div>
          </div>
          {/* Score */}
          <div
            style={{
              background: "rgba(245,158,11,.1)",
              border: "1px solid rgba(245,158,11,.3)",
              borderRadius: 12,
              padding: "8px 4px",
              textAlign: "center",
            }}>
            <div
              style={{
                color: "#F59E0B",
                fontWeight: 900,
                fontSize: "clamp(16px,4vw,22px)",
                lineHeight: 1,
              }}>
              {score}
            </div>
            <div
              style={{
                color: "#F59E0B",
                fontSize: 9,
                fontWeight: 700,
                marginTop: 2,
                opacity: 0.7,
              }}>
              BALL
            </div>
          </div>
        </div>

        {/* Progress bar */}
        <div
          style={{
            maxWidth: 700,
            margin: "10px auto 0",
            height: 4,
            background: "#1E2D45",
            borderRadius: 99,
            overflow: "hidden",
          }}>
          <div
            style={{
              height: "100%",
              width: `${(timeLeft / START_SECONDS) * 100}%`,
              background: `linear-gradient(90deg,${timerColor},${timerColor}99)`,
              borderRadius: 99,
              transition: "width 1s linear, background .5s",
            }}
          />
        </div>
      </div>

      {/* ── QUESTION AREA ── */}
      <div
        style={{
          flex: 1,
          overflowY: "auto",
          padding: "16px",
          maxWidth: 700,
          margin: "0 auto",
          width: "100%",
        }}>
        {question && (
          <>
            {/* Question text */}
            <div
              style={{
                background: "#111827",
                border: "1px solid #1E2D45",
                borderRadius: 16,
                padding: "16px 18px",
                marginBottom: 14,
              }}>
              <p
                style={{
                  color: "#E2E8F0",
                  fontWeight: 700,
                  fontSize: "clamp(13px,2.5vw,16px)",
                  lineHeight: 1.5,
                  margin: 0,
                }}>
                {question.question}
              </p>
            </div>

            {/* Image */}
            {question.photo_url && (
              <div
                style={{
                  borderRadius: 14,
                  overflow: "hidden",
                  marginBottom: 14,
                  background: "#0D1526",
                  border: "1px solid #1E2D45",
                }}>
                <img
                  src={`${IMG_BASE}/${question.photo_url}`}
                  alt="Q"
                  onLoad={() => setImgLoaded(true)}
                  onError={(e) => (e.currentTarget.src = DEFAULT_IMG)}
                  style={{
                    width: "100%",
                    maxHeight: 220,
                    objectFit: "contain",
                    display: "block",
                    opacity: imgLoaded ? 1 : 0,
                    transition: "opacity .3s",
                  }}
                />
              </div>
            )}

            {/* Choices */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 8,
                marginBottom: 14,
              }}>
              {question.choices.map((choice, idx) => {
                const isSelected = selected === choice.order_index;
                const isRight =
                  choice.order_index === question.correct_answer_index;
                let bg = "#111827",
                  border = "#1E2D45",
                  color = "#CBD5E1";

                if (selected !== null) {
                  if (isRight) {
                    bg = "rgba(34,197,94,.12)";
                    border = "rgba(34,197,94,.5)";
                    color = "#86EFAC";
                  } else if (isSelected) {
                    bg = "rgba(239,68,68,.12)";
                    border = "rgba(239,68,68,.5)";
                    color = "#FCA5A5";
                  } else {
                    bg = "#0D1526";
                    border = "#1E2D45";
                    color = "#475569";
                  }
                }

                return (
                  <button
                    key={choice.id}
                    disabled={selected !== null}
                    onClick={() => handleAnswer(choice.order_index, choice.id)}
                    className={selected === null ? "choice-hover" : ""}
                    style={{
                      background: bg,
                      border: `1.5px solid ${border}`,
                      borderRadius: 14,
                      padding: "12px 14px",
                      textAlign: "left",
                      cursor: selected !== null ? "default" : "pointer",
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 10,
                      transition: "all .15s",
                    }}>
                    <span
                      style={{
                        width: 26,
                        height: 26,
                        borderRadius: 8,
                        background:
                          isRight && selected !== null ? "rgba(34,197,94,.2)"
                          : isSelected && selected !== null ?
                            "rgba(239,68,68,.2)"
                          : "rgba(255,255,255,.06)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontWeight: 900,
                        fontSize: 12,
                        color: color,
                        flexShrink: 0,
                      }}>
                      {LABELS[idx]}
                    </span>
                    <span
                      style={{
                        color,
                        fontSize: "clamp(12px,2vw,14px)",
                        fontWeight: 600,
                        lineHeight: 1.4,
                      }}>
                      {choice.text}
                    </span>
                  </button>
                );
              })}
            </div>

            {/* Result banner */}
            {selected !== null && (
              <div
                className="slide-in"
                style={{
                  borderRadius: 14,
                  overflow: "hidden",
                  marginBottom: explanation ? 10 : 0,
                }}>
                <div
                  style={{
                    background:
                      isCorrect ? "rgba(34,197,94,.12)" : "rgba(239,68,68,.12)",
                    border: `1px solid ${isCorrect ? "rgba(34,197,94,.4)" : "rgba(239,68,68,.4)"}`,
                    borderRadius: 14,
                    padding: "12px 16px",
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                  }}>
                  {isCorrect ?
                    <CheckCircle size={20} color="#22C55E" />
                  : <XCircle size={20} color="#EF4444" />}
                  <div style={{ flex: 1 }}>
                    <span
                      style={{
                        color: isCorrect ? "#86EFAC" : "#FCA5A5",
                        fontWeight: 800,
                        fontSize: 14,
                      }}>
                      {isCorrect ? "To'g'ri javob!" : "Xato javob!"}
                    </span>
                  </div>
                  {isCorrect && (
                    <span
                      className="bounce"
                      style={{
                        color: "#22C55E",
                        fontWeight: 900,
                        fontSize: 14,
                        background: "rgba(34,197,94,.15)",
                        padding: "4px 10px",
                        borderRadius: 8,
                      }}>
                      +20 ball
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* Explanation */}
            {explanation && selected !== null && (
              <div
                className="slide-in"
                style={{
                  background: "rgba(245,158,11,.06)",
                  border: "1px solid rgba(245,158,11,.2)",
                  borderRadius: 14,
                  padding: "12px 16px",
                  marginBottom: 10,
                  display: "flex",
                  gap: 10,
                }}>
                <span style={{ fontSize: 16 }}>💡</span>
                <p
                  style={{
                    color: "#CBD5E1",
                    fontSize: 13,
                    lineHeight: 1.5,
                    margin: 0,
                  }}>
                  {explanation}
                </p>
              </div>
            )}
          </>
        )}
      </div>

      {showLeaderboard && (
        <LeaderboardModal
          data={leaderboard}
          userId={user?.id?.toString()}
          onClose={() => setShowLeaderboard(false)}
        />
      )}
    </div>
  );
};

// ── Leaderboard Modal ──
const LeaderboardModal: React.FC<{
  data: LeaderEntry[];
  userId?: string;
  onClose: () => void;
}> = ({ data, userId, onClose }) => (
  <div
    style={{
      position: "fixed",
      inset: 0,
      background: "rgba(0,0,0,.8)",
      display: "flex",
      alignItems: "flex-end",
      justifyContent: "center",
      zIndex: 999,
      padding: "0 0 0 0",
    }}
    onClick={onClose}>
    <div
      style={{
        background: "#111827",
        border: "1px solid #1E2D45",
        borderRadius: "24px 24px 0 0",
        padding: "20px",
        width: "100%",
        maxWidth: 500,
        maxHeight: "80vh",
        overflowY: "auto",
      }}
      onClick={(e) => e.stopPropagation()}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 16,
        }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Trophy size={18} color="#F59E0B" />
          <span style={{ color: "#fff", fontWeight: 900, fontSize: 16 }}>
            Top o'yinchilar
          </span>
        </div>
        <button
          onClick={onClose}
          style={{
            background: "transparent",
            border: "none",
            color: "#64748B",
            fontSize: 20,
            cursor: "pointer",
          }}>
          ✕
        </button>
      </div>

      {data.length === 0 ?
        <p style={{ color: "#64748B", textAlign: "center", padding: "32px 0" }}>
          Hali natijalar yo'q
        </p>
      : data.map((e, i) => {
          const isMe = e.user?.id === userId;
          const medals = ["🥇", "🥈", "🥉"];
          return (
            <div
              key={i}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                padding: "12px 14px",
                borderRadius: 12,
                background: isMe ? "rgba(245,158,11,.1)" : "transparent",
                border:
                  isMe ?
                    "1px solid rgba(245,158,11,.3)"
                  : "1px solid transparent",
                marginBottom: 8,
              }}>
              <span style={{ fontSize: 18, minWidth: 28, textAlign: "center" }}>
                {medals[i] || `${i + 1}`}
              </span>
              <div style={{ flex: 1 }}>
                <div
                  style={{
                    color: isMe ? "#F59E0B" : "#E2E8F0",
                    fontWeight: isMe ? 800 : 600,
                    fontSize: 14,
                  }}>
                  {isMe ? "Siz" : e.user?.name || "Noma'lum"}{" "}
                </div>
                <div style={{ color: "#64748B", fontSize: 11 }}>
                  {e.correctCount} ta savol{" "}
                </div>
              </div>
              <div style={{ color: "#F59E0B", fontWeight: 900, fontSize: 16 }}>
                {e.totalScore}
              </div>
            </div>
          );
        })
      }
    </div>
  </div>
);

// ── Styles ──
const S: Record<string, React.CSSProperties> = {
  page: {
    minHeight: "100vh",
    background: "#0A0E1A",
    display: "flex",
    flexDirection: "column",
    fontFamily: "'Plus Jakarta Sans', sans-serif",
    color: "#E2E8F0",
  },
  nav: {
    padding: "12px 16px",
    display: "flex",
    alignItems: "center",
    borderBottom: "1px solid #1E2D45",
  },
  backBtn: {
    background: "transparent",
    border: "1px solid #1E2D45",
    borderRadius: 10,
    color: "#64748B",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: 36,
    height: 36,
  },
};

const css = `
  @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800;900&display=swap');
  @keyframes fadeUp { from{opacity:0;transform:translateY(12px)} to{opacity:1;transform:translateY(0)} }
  @keyframes slideIn { from{opacity:0;transform:translateY(8px)} to{opacity:1;transform:translateY(0)} }
  @keyframes bounce { 0%,100%{transform:scale(1)} 50%{transform:scale(1.15)} }
  @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.6} }
  @keyframes bonusPop { 0%{opacity:0;transform:translateX(-50%) translateY(0)} 50%{opacity:1} 100%{opacity:0;transform:translateX(-50%) translateY(-28px)} }
  @keyframes pulseShadow { 0%,100%{box-shadow:0 0 0 0 rgba(245,158,11,.4)} 50%{box-shadow:0 0 0 12px rgba(245,158,11,0)} }
  .slide-in { animation: slideIn .25s ease forwards; }
  .bounce { animation: bounce .4s ease; }
  .bonus-anim { animation: bonusPop 1.2s ease forwards; pointer-events:none; }
  .choice-hover:hover { border-color: rgba(59,130,246,.5) !important; background: rgba(59,130,246,.05) !important; transform: translateY(-1px); }
  .choice-hover { transition: all .15s ease; }
  .pulse-btn { animation: pulseShadow 2s ease infinite; }

  @media (max-width: 480px) {
    .choice-grid { grid-template-columns: 1fr !important; }
  }
`;

export default TimeAttackPage;
